import { buildPeriodQueryRange, fuzzyFilter, isWithinDateRange, useDebounce } from '@practium/shared';
import type { FilterGroup } from '@practium/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetAdviceMaintenance } from '../../api/hooks/maintenance';
import { useGetAdviceFoliar, useGetRecommendationsStats } from '../../api/hooks/recommendations';
import { useGetAdviceCorrection } from '../../api/hooks/recommendations/useGetAdviceCorrection';
import { mapListPageLineChart, mapListPagePieData } from '../../dashboard/utils/mapListPageCharts';
import type {
	RecommendationsPermissions,
	RecommendationsTab,
	RecommendationTableRow,
} from '../types';

const PAGE_SIZE = 10;

const getDisplayName = (value: unknown): string => {
	if (!value) return '';
	if (typeof value === 'string') return value;
	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const label = record.name ?? record.title ?? record._id ?? record.id;
		return typeof label === 'string' ? label : '';
	}
	return String(value);
};

const getDisplayId = (value: unknown): string => {
	if (!value) return '';
	if (typeof value === 'string') return value;
	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const id = record._id ?? record.id;
		return typeof id === 'string' ? id : '';
	}
	return '';
};

const PERIOD_FILTER_GROUP: FilterGroup = {
	label: 'Período',
	options: [
		{ value: 'periodo:15', label: 'Últimos 15 dias' },
		{ value: 'periodo:30', label: 'Últimos 30 dias' },
		{ value: 'periodo:custom', label: 'Personalizado' },
	],
};

const buildFilterGroups = (rows: RecommendationTableRow[]): FilterGroup[] => [
	{
		label: 'Área',
		options: Array.from(
			new Map(
				rows
					.filter((row) => row.area && row.areaId)
					.map((row) => [row.areaId as string, row.area])
			)
		)
			.sort(([, a], [, b]) => a.localeCompare(b, 'pt-BR'))
			.map(([areaId, area]) => ({ value: `area:${areaId}`, label: area })),
	},
	PERIOD_FILTER_GROUP,
];

export interface UseRecommendationsPageProps {
	permissions: RecommendationsPermissions;
	areaId?: string | string[];
	forBinding?: string;
	initialTab?: RecommendationsTab;
}

export const useRecommendationsPage = ({
	permissions,
	areaId,
	forBinding,
	initialTab = 'correction',
}: UseRecommendationsPageProps) => {
	const [activeTab, setActiveTab] = useState<RecommendationsTab>(initialTab);
	const [searchQuery, setSearchQuery] = useState('');
	const [sortAsc, setSortAsc] = useState(false);
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [customDateFrom, setCustomDateFrom] = useState('');
	const [customDateTo, setCustomDateTo] = useState('');
	const [page, setPage] = useState(1);
	const searchContext = useMemo(
		() => ({ tab: activeTab, query: searchQuery }),
		[activeTab, searchQuery]
	);
	const debouncedSearch = useDebounce(searchContext, 400);
	const debouncedSearchQuery =
		searchQuery.trim() && debouncedSearch.tab === activeTab ? debouncedSearch.query : '';

	const areaFilters = useMemo(
		() => filterValues.filter((v) => v.startsWith('area:')).map((v) => v.slice(5)),
		[filterValues]
	);

	const periodoFilters = useMemo(
		() => filterValues.filter((v) => v.startsWith('periodo:')).map((v) => v.slice(8)),
		[filterValues]
	);

	const { startDate, endDate } = useMemo(
		() => buildPeriodQueryRange(periodoFilters, customDateFrom, customDateTo),
		[periodoFilters, customDateFrom, customDateTo]
	);
	const apiFilter = debouncedSearchQuery.trim() || undefined;
	const remoteSortAsc = sortAsc ? true : undefined;
	const remoteAreaId = areaFilters.length > 0 ? areaFilters : undefined;

	const chartsQuery = useGetRecommendationsStats({
		areaId: areaId ?? remoteAreaId,
		startDate,
		endDate,
		forBinding,
	});

	const foliarQuery = useGetAdviceFoliar({
		page,
		limit: PAGE_SIZE,
		enabled: activeTab === 'foliar',
		areaId: activeTab === 'foliar' ? remoteAreaId : undefined,
		startDate: activeTab === 'foliar' ? startDate : undefined,
		endDate: activeTab === 'foliar' ? endDate : undefined,
		filter: activeTab === 'foliar' ? apiFilter : undefined,
		sortAsc: activeTab === 'foliar' ? remoteSortAsc : undefined,
	});
	const correctionQuery = useGetAdviceCorrection({
		page,
		limit: PAGE_SIZE,
		enabled: activeTab === 'correction',
		areaId: activeTab === 'correction' ? remoteAreaId : undefined,
		startDate: activeTab === 'correction' ? startDate : undefined,
		endDate: activeTab === 'correction' ? endDate : undefined,
		filter: activeTab === 'correction' ? apiFilter : undefined,
		sortAsc: activeTab === 'correction' ? remoteSortAsc : undefined,
	});
	const maintenanceQuery = useGetAdviceMaintenance({
		page,
		limit: PAGE_SIZE,
		enabled: activeTab === 'maintenance',
		areaId: activeTab === 'maintenance' ? remoteAreaId : undefined,
		startDate: activeTab === 'maintenance' ? startDate : undefined,
		endDate: activeTab === 'maintenance' ? endDate : undefined,
		filter: activeTab === 'maintenance' ? apiFilter : undefined,
		sortAsc: activeTab === 'maintenance' ? remoteSortAsc : undefined,
	});
	const { data: foliarRows = [] } = foliarQuery;
	const { data: correctionRows = [] } = correctionQuery;
	const { data: maintenanceRowsData = [] } = maintenanceQuery;
	const activeQuery =
		activeTab === 'maintenance'
			? maintenanceQuery
			: activeTab === 'foliar'
				? foliarQuery
				: correctionQuery;
	const chartsData = chartsQuery.isPlaceholderData ? undefined : chartsQuery.data;
	const foliarTableRows = useMemo<RecommendationTableRow[]>(() => {
		return foliarRows.map((item) => ({
			_id: item._id ?? item._id ?? '',
			name: item.name,
			prop: getDisplayName(item.prop),
			area: getDisplayName(item.area),
			areaId: getDisplayId(item.area),
			sector:
				item.sectors && item.sectors.length > 2
					? `${item.sectors
						.slice(0, 2)
						.map(getDisplayName)
						.join(', ')}...`
					: (item.sectors?.map(getDisplayName).join(', ') ?? ''),
			date: item.applicationDate,
		}));
	}, [foliarRows]);

	const correctionTableRows = useMemo<RecommendationTableRow[]>(() => {
		return correctionRows.map((item) => ({
			_id: item._id ?? item._id ?? '',
			name: item.name,
			prop: getDisplayName(item.prop),
			area: getDisplayName(item.area),
			areaId: getDisplayId(item.area),
			sector:
				item.sectors && item.sectors.length > 2
					? `${item.sectors
						.slice(0, 2)
						.map(getDisplayName)
						.join(', ')}...`
					: (item.sectors?.map(getDisplayName).join(', ') ?? ''),
			date: item.createdAt,
		}));
	}, [correctionRows]);

	const maintenanceTableRows = useMemo<RecommendationTableRow[]>(() => {
		return maintenanceRowsData.map((item) => ({
			_id: item._id ?? item._id ?? '',
			name: item.name,
			prop: getDisplayName(item.prop),
			area: getDisplayName(item.area),
			areaId: getDisplayId(item.area),
			sector:
				item.sectors && item.sectors.length > 2
					? `${item.sectors
						.slice(0, 2)
						.map(getDisplayName)
						.join(', ')}...`
					: (item.sectors?.map(getDisplayName).join(', ') ?? ''),
			date: item.createdAt,
		}));
	}, [maintenanceRowsData]);

	const activeTableRows = useMemo(() => {
		if (activeTab === 'maintenance') return maintenanceTableRows;
		if (activeTab === 'foliar') return foliarTableRows;
		return correctionTableRows;
	}, [activeTab, correctionTableRows, maintenanceTableRows, foliarTableRows]);

	const filterGroups = useMemo(() => buildFilterGroups(activeTableRows), [activeTableRows]);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearchQuery, filterValues, customDateFrom, customDateTo, sortAsc, activeTab]);

	const handleTabChange = (tab: RecommendationsTab) => {
		setActiveTab(tab);
		setSearchQuery('');
		setFilterValues([]);
		setCustomDateFrom('');
		setCustomDateTo('');
		setPage(1);
	};

	const processRows = useCallback(
		(rows: RecommendationTableRow[] = []) => {
			let result = fuzzyFilter(rows, debouncedSearchQuery, (r) =>
				[r.name, r.prop, r.area, r.sector, r.date].filter(Boolean).join(' ')
			);

			if (areaFilters.length > 0) {
				result = result.filter((r) => r.areaId && areaFilters.includes(r.areaId));
			}

			if (periodoFilters.length > 0) {
				result = result.filter((r) => isWithinDateRange(r.date, startDate, endDate));
			}

			return result;
		},
		[debouncedSearchQuery, areaFilters, periodoFilters.length, startDate, endDate]
	);

	const processedCorrection = useMemo(
		() => processRows(correctionTableRows),
		[correctionTableRows, processRows]
	);
	const processedMaintenance = useMemo(
		() => processRows(maintenanceTableRows),
		[maintenanceTableRows, processRows]
	);
	const processedFoliar = useMemo(
		() => processRows(foliarTableRows),
		[foliarTableRows, processRows]
	);

	const totalPages =
		activeQuery.isPlaceholderData || activeQuery.isError
			? 1
			: (activeQuery.pagination?.totalPages ?? 1);

	const filteredCorrection = useMemo(
		() => processedCorrection,
		[processedCorrection]
	);

	const filteredMaintenance = useMemo(
		() => processedMaintenance,
		[processedMaintenance]
	);

	const filteredFoliar = useMemo(
		() => processedFoliar,
		[processedFoliar]
	);

	const recommendationsPieData = useMemo(() => {
		const items = Array.isArray(chartsData?.pie)
			? chartsData.pie
			: chartsData?.pie?.items;
		return mapListPagePieData(items);
	}, [chartsData]);

	const {
		lineData: recommendationsLineData,
		mainValue: chartMainValue,
		trendValue: chartTrendValue,
		trendDirection: chartTrendDirection,
		footerText: recommendationsFooterText,
	} = useMemo(() => mapListPageLineChart(chartsData?.line), [chartsData]);

	return {
		activeTab,
		setActiveTab: handleTabChange,
		filteredCorrection,
		filteredMaintenance,
		filteredFoliar,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort: () => setSortAsc((v) => !v),
		filterValues,
		setFilterValues,
		filterGroups,
		customDateFrom,
		setCustomDateFrom,
		customDateTo,
		setCustomDateTo,
		page,
		setPage,
		totalPages,
		isTableLoading: activeQuery.isPending || activeQuery.isPlaceholderData,
		isTableFetching: activeQuery.isFetching,
		isTableError: activeQuery.isError,
		refetchTable: async () => {
			await activeQuery.refetch();
		},
		isChartsLoading: chartsQuery.isPending || chartsQuery.isPlaceholderData,
		isChartsFetching: chartsQuery.isFetching,
		isChartsError: chartsQuery.isError,
		permissions,
		recommendationsPieData,
		recommendationsLineData,
		chartMainValue,
		chartTrendValue,
		chartTrendDirection,
		recommendationsFooterText,
	};
};
